import type { LocationQueryRaw, Router } from 'vue-router'
import CONSTANTS from '@/constants'
import { resolveCheckoutReturn } from '@/helpers/checkoutReturn'

const { AUTH_PROTOCOL_KEYS } = CONSTANTS

// Checkout only needs selection, promotion, subscription-view, attribution,
// and validated purchase-return context. This list prevents OAuth protocol
// values (and any future unknown callback values) from being propagated by
// account/payment/shop navigation.
const CHECKOUT_QUERY_KEYS = [
  'product_selected',
  'products',
  'plan',
  'coupon',
  'sub_view_selected',
  'special_offer',
  'available_plans',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_adgroup',
  'utm_promo',
  'utm_purpose',
  'utm_network',
  'utm_placement',
  'gclid',
  'fbclid',
  'gcl_id',
  'fbcl_id',
  'return_to',
] as const

const CHECKOUT_QUERY_KEY_SET = new Set<string>(CHECKOUT_QUERY_KEYS)
// Purchase return_to is separate from the same-origin auth return target. Keep
// only one content path accepted by the shared streaming-origin validator.
function isAllowedCheckoutQueryValue(key: string, value: unknown) {
  return (
    CHECKOUT_QUERY_KEY_SET.has(key) &&
    (key !== 'return_to' ||
      Boolean(resolveCheckoutReturn(value, process.env.VUE_APP_STREAMING_URL)))
  )
}

const AUTH_PROTOCOL_KEY_PATTERN = new RegExp(
  `(?:^|[^A-Za-z0-9_])(?:${AUTH_PROTOCOL_KEYS.join('|')})=`,
  'i'
)

/**
 * Keeps checkout selection, promotion, view, attribution, and validated
 * purchase-return query values.
 * Unknown keys and authentication protocol parameters are removed so they are
 * not propagated to subsequent checkout routes.
 */
export function sanitizeCheckoutQuery(
  query: Record<string, unknown> | LocationQueryRaw = {}
): LocationQueryRaw {
  return Object.entries(query).reduce((sanitized, [key, value]) => {
    if (isAllowedCheckoutQueryValue(key, value) && value !== undefined) {
      sanitized[key] = value as LocationQueryRaw[string]
    }
    return sanitized
  }, {} as LocationQueryRaw)
}

/**
 * Applies the checkout query allowlist to URLSearchParams while preserving
 * repeated allowed values and their order.
 */
export function sanitizeCheckoutSearchParams(params: URLSearchParams) {
  const sanitized = new URLSearchParams()
  params.forEach((value, key) => {
    if (
      isAllowedCheckoutQueryValue(key, value) &&
      (key !== 'return_to' || params.getAll(key).length === 1)
    ) {
      sanitized.append(key, value)
    }
  })
  return sanitized
}

/**
 * Returns true when a route query contains at least one key outside the
 * checkout allowlist and therefore requires replacement.
 */
export function checkoutQueryNeedsSanitization(query: Record<string, unknown> = {}) {
  return Object.entries(query).some(([key, value]) => !isAllowedCheckoutQueryValue(key, value))
}

/**
 * Detects ASCII control characters, including DEL, that make URL parsing or
 * downstream decoding unsafe or inconsistent.
 */
export function containsControlCharacters(value: string) {
  return Array.from(value).some(character => {
    const codePoint = character.charCodeAt(0)
    return codePoint <= 31 || codePoint === 127
  })
}

/**
 * Preserves ordinary checkout anchors. Malformed encoding, control characters,
 * and authentication protocol values cause the complete hash to be removed.
 */
export function sanitizeCheckoutHash(hash: string = '') {
  if (!hash) return ''

  try {
    const decodedHash = decodeURIComponent(hash)
    return containsControlCharacters(decodedHash) || AUTH_PROTOCOL_KEY_PATTERN.test(decodedHash)
      ? ''
      : hash
  } catch {
    return ''
  }
}

/**
 * Builds a checkout route location that keeps the caller's route target
 * (name, path, or params) and carries only the sanitized query and hash from
 * the source route.
 */
export function buildSanitizedCheckoutLocation<T extends object>(
  target: T,
  source: { query?: Record<string, unknown> | LocationQueryRaw; hash?: string }
) {
  return {
    ...target,
    query: sanitizeCheckoutQuery(source.query),
    hash: sanitizeCheckoutHash(source.hash),
  }
}

/**
 * Replaces the active route with its sanitized query and hash when necessary.
 *
 * @returns true when a replacement was performed; false when no router route
 * was available or the current location was already safe.
 */
export async function replaceUnsafeCheckoutRoute(router?: Router) {
  const currentRoute = router?.currentRoute?.value
  if (!currentRoute) return false

  const sanitizedHash = sanitizeCheckoutHash(currentRoute.hash)
  if (!checkoutQueryNeedsSanitization(currentRoute.query) && sanitizedHash === currentRoute.hash) {
    return false
  }

  await router.replace({
    path: currentRoute.path,
    query: sanitizeCheckoutQuery(currentRoute.query),
    hash: sanitizedHash,
  })
  return true
}

export { CHECKOUT_QUERY_KEYS }
